import React, {
  createContext,
  useState,
  useEffect,
  ReactNode,
  FC,
  useRef,
} from "react";
import useCognito from "../hooks/auth/useCognito";
import { useSubscriptionStore, useUserStore } from "../state";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CognitoUserSession,
  CognitoUserAttribute,
} from "amazon-cognito-identity-js";
import { useSubUserStore } from "../state/useSubUserStore";
import jwtDecode from "jwt-decode";
import { getCurrentMonthData } from "../utils/DateTimeRange";
import useGoogleAuth from "../hooks/auth/useGoogleAuth";
import { ROLES, STORAGE_REFRESH_TOKEN, STORAGE_TOKEN } from "../constants";
import Cookies from "js-cookie";
import { Modal } from "../components/common/Modal";
import { ChangePassword } from "../components/core/ChangePassword";
import BusinessTypeSelector from "../components/auth/BusinessTypeModal";
import { SubscriptionModal } from "../components/common/Subscription/SubscriptionModal";
import { useBudgetStore } from "../state/useBudgetStore";
import { useUserDataLoader } from "../hooks/users/useUserDataLoader";
import {
  DecodedToken,
  getParsedCookie,
  getToken,
  handleTokenAndFetchUserData,
  isTokenExpired,
  refreshToken,
} from "../utils/token";
import { useCompanyStore } from "../state/useCompanyStore";
import { WhatsAppProvider } from "./WhatsappProvider";
import LoginLoader from "../components/auth/LoginLoader";
import { ToastProvider } from "../components/common/Banner/ToastContainer";
import { ICreateSalesData } from "../@types";

interface AuthContextType {
  user: CognitoUserSession | null;
  loading: boolean;
  error: string | null;
  login: (username: string, password: string) => Promise<void>;
  googleLogin: () => void;
  isGoogleAuthLoading: boolean;
  handleGoogleCallback: () => Promise<void>;
  logout: () => void;
  register: (
    username: string,
    password: string,
    attributes: CognitoUserAttribute[],
  ) => Promise<void>;
}

interface AuthProviderProps {
  children: ReactNode;
}

export const UseAuthContext = createContext<AuthContextType>({
  user: null,
  loading: false,
  error: null,
  isGoogleAuthLoading: false,
  googleLogin: () => {
    throw new Error("googleLogin function not implemented");
  },
  login: async () => {
    throw new Error("login function not implemented");
  },
  logout: () => {},
  register: async () => {
    throw new Error("register function not implemented");
  },
  handleGoogleCallback: async () => {
    throw new Error("handleGoogleCallback function not implemented");
  },
});

// This hook will protect the route access based on user authentication.
function useProtectedRoute(handleLogout: () => void) {
  const token = getToken();
  React.useEffect(() => {
    if (token && isTokenExpired(token)) {
      refreshToken(handleLogout);
    }
  }, []);
}

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const {
    user: cognitoUser,
    session,
    login: cognitoLogin,
    logout: cognitoLogout,
    register: cognitoRegister,
    loading: authLoading,
    error: authError,
  } = useCognito();

  const { handleGoogleSignIn, handleCodeExchange } = useGoogleAuth();
  const {
    fetchData,
    businessModalVisible,
    showFeatures,
    isPasswordModalShown,
    setBusinessModalVisible,
    setIsPasswordModalShown,
  } = useUserDataLoader();
  const { fetchBudgets } = useBudgetStore();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const redirectUrl = queryParams.get("redirect");
  const [isGoogleAuthLoading, setIsGoogleAuthLoading] = useState(false);

  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(authError);
  const { setUser, setRole } = useUserStore();
  const { setCompanyBankAccounts } = useCompanyStore();
  const { setSubscriptionData } = useSubscriptionStore();
  const isPasswordChanged = useSubUserStore((state) => state.isPasswordChanged);
  const isRootUser = useSubUserStore((state) => state.isRootUser);
  const hasGoogleCallbackExecuted = useRef(false);
  /**
   * Fetches user data after successful authentication.
   */

  useEffect(() => {
    if (cognitoUser && session) {
      const token = session.getIdToken().getJwtToken();
      const decodedToken = jwtDecode<DecodedToken>(token);
      const userId = decodedToken["custom:custom_userid"];
      fetchData(userId);
    }
  }, [cognitoUser, session]);

  /**
   * Modified login function that authenticates with Cognito
   * and fetches user data upon successful authentication.
   * @param username
   * @param password
   */
  const login = async (username: string, password: string): Promise<void> => {
    setLoading(true);
    setError(null);

    // ✅ Fire GTM event for login attempt
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "login_attempt",
      method: "email_auth",
    });

    try {
      const sessionResult: CognitoUserSession = await cognitoLogin(
        username,
        password,
      );
      const token = sessionResult.getIdToken().getJwtToken();

      const { userId, groups } = await handleTokenAndFetchUserData(
        token,
        setRole,
      );

      await fetchData(userId);
      await fetchBudgets();
      const type = Cookies.get("type") || "";
      const parsedInvoiceData =
        getParsedCookie<ICreateSalesData>("invoiceData");

      if (groups.includes(ROLES.ADMIN)) {
        if (redirectUrl) {
          navigate(`${redirectUrl}`);
        } else if (type && parsedInvoiceData) {
          navigate("/new-user");
        } else {
          navigate("/dashboard");
        }
      } else if (groups.includes(ROLES.OPERATOR)) {
        navigate("/operator-dashboard");
      } else if (groups.includes(ROLES.ACCOUNTANT)) {
        navigate("/operator-dashboard");
      } else {
        setError("You do not have permission to access this application.");
      }

      // ✅ Fire GTM event for successful login
      window.dataLayer.push({
        event: "login_success",
        method: "email_auth",
        user_id: userId,
      });
    } catch (err: any) {
      setError(err.message || "An unknown error occurred during login.");

      // ✅ Fire GTM event for login error
      window.dataLayer.push({
        event: "login_error",
        error_message: err.message,
      });
    } finally {
      setLoading(false);
    }
  };
  /**
   * Modified logout function that signs out from Cognito
   * and clears user data from the store.
   */
  const logout = (): void => {
    cognitoLogout();

    setUser(null);
    setRole([]);
    setSubscriptionData(null);
    setCompanyBankAccounts([]);
    setLoading(false);
    setError(null);
    navigate("/login");

    // ✅ Fire GTM event for logout
    window.dataLayer.push({
      event: "logout",
    });
  };

  const logoutExistingUser = (): void => {
    cognitoLogout();

    setUser(null);
    setRole([]);
    setSubscriptionData(null);
    setCompanyBankAccounts([]);
    setLoading(false);
    setError(null);
  };

  const googleLogin = () => {
    setIsGoogleAuthLoading(true);
    // ✅ Fire GTM event for Google login attempt
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "login_attempt",
      method: "google_auth",
    });

    handleGoogleSignIn();
  };

  const handleGoogleCallback = async () => {
    if (hasGoogleCallbackExecuted.current) {
      return;
    }

    hasGoogleCallbackExecuted.current = true;

    const searchParams = new URLSearchParams(window.location.search);

    setLoading(true);

    const code = searchParams.get("code");
    const token = searchParams.get("token");
    const refreshToken = searchParams.get("refreshToken");
    const navigationParam = searchParams.get("state"); // Fetching from URL query params

    try {
      // If code and navigationParam are present, handle the "freeInvoice" logic
      window.dataLayer = window.dataLayer || [];
      if (code && navigationParam === "freeInvoice") {
        // ✅ Fire GTM event for Google Sign-in attempt
        window.dataLayer?.push({
          event: "google_login_attempt",
          conversion_type: "google_auth",
        });

        const data = await handleCodeExchange(code);
        const { id_token, refresh_token } = data;

        if (id_token) {
          localStorage.setItem(STORAGE_TOKEN, id_token);
          localStorage.setItem(STORAGE_REFRESH_TOKEN, refresh_token);

          const decodedToken: DecodedToken = jwtDecode(id_token);
          const userId = decodedToken["custom:custom_userid"];
          const user = await fetchData(userId);

          const groups = decodedToken["cognito:groups"] || [];
          setRole(groups);
          await fetchData(userId);

          // ✅ Fire GTM event for successful Google Sign-in
          window.dataLayer.push({
            event: "google_login_success",
            conversion_type: "google_auth",
          });

          navigate("/new-user");
        }
        Cookies.remove("navigation");
      }

      // Check for token and refreshToken, and handle the exchange
      if (token && refreshToken) {
        localStorage.setItem(STORAGE_TOKEN, token);
        localStorage.setItem(STORAGE_REFRESH_TOKEN, refreshToken);

        const decodedToken: DecodedToken = jwtDecode(token);
        const userId = decodedToken["custom:custom_userid"];
        const user = await fetchData(userId);

        const groups = decodedToken["cognito:groups"] || [];
        setRole(groups);
        await fetchData(userId);

        // ✅ Fire GTM event for successful Google Sign-in
        window.dataLayer.push({
          event: "google_login_success",
          conversion_type: "google_auth",
        });

        navigate("/new-user");
      }

      // If only the code is available, handle the exchange
      if (code) {
        // ✅ Fire GTM event for Google Sign-in attempt
        window.dataLayer.push({
          event: "google_login_attempt",
          conversion_type: "google_auth",
        });

        setLoading(true);
        Cookies.set("googleAuthCallback", "true");

        const data = await handleCodeExchange(code);
        const { id_token, refresh_token } = data;

        if (id_token) {
          localStorage.setItem(STORAGE_TOKEN, id_token);
          localStorage.setItem(STORAGE_REFRESH_TOKEN, refresh_token);

          const decodedToken: DecodedToken = jwtDecode(id_token);
          const userId = decodedToken["custom:custom_userid"];
          const user = await fetchData(userId);
          setUser(user);

          const groups = decodedToken["cognito:groups"] || [];
          setRole(groups);
          await fetchData(userId);

          // ✅ Fire GTM event for successful Google Sign-in
          window.dataLayer.push({
            event: "google_login_success",
            conversion_type: "google_auth",
          });

          navigate("/dashboard");
        }
      }
    } catch (error) {
      console.error("Error during Google Callback:", error);

      // ✅ Fire GTM event for Google Sign-in error
      window.dataLayer.push({
        event: "google_login_error",
        error_message: "Unknown error",
      });
    } finally {
      setLoading(false);
      Cookies.remove("googleAuthCallback");
      Cookies.remove("googleAuthCallback", { path: "/" });
    }
  };

  /**
   * Modified register function that registers the user with Cognito
   * and can optionally fetch user data or handle post-registration steps.
   * @param username
   * @param password
   * @param attributes
   */
  const register = async (
    username: string,
    password: string,
    attributes: CognitoUserAttribute[],
  ): Promise<void> => {
    setLoading(true);
    setError(null);

    // ✅ Fire GTM event for registration attempt
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "signup_attempt",
      method: "email_auth",
    });

    try {
      await cognitoRegister(username, password, attributes);
      // ✅ Fire GTM event for successful registration
      window.dataLayer.push({
        event: "signup_success",
        method: "email_auth",
        user_email: username,
      });
      navigate("/confirm-account");
    } catch (err: any) {
      setError(err.message || "An unknown error occurred during registration.");
      // ✅ Fire GTM event for signup error
      window.dataLayer.push({
        event: "signup_error",
        error_message: err.message,
      });

      throw err;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authError) {
      setError(authError);
    }
  }, [authError]);

  useEffect(() => {
    const checkGoogleAuth = async () => {
      const searchParams = new URLSearchParams(window.location.search);
      const code = searchParams.get("code");
      const token = searchParams.get("token");

      if (code || token) {
        await handleGoogleCallback();
      }
    };

    checkGoogleAuth();
  }, []);

  // useProtectedRoute(logout);

  return (
    <UseAuthContext.Provider
      value={{
        user: session,
        loading,
        error,
        login,
        googleLogin,
        handleGoogleCallback,
        isGoogleAuthLoading,
        logout,
        register,
      }}
    >
      {/* Only show loader when actively processing Google auth */}
      {isGoogleAuthLoading ? (
        <LoginLoader />
      ) : (
        <>
          <ToastProvider>
            <WhatsAppProvider
              phoneNumber="23408037889887"
              message="Hi, I need support!"
            >
              {children}
            </WhatsAppProvider>
          </ToastProvider>

          {/* Business Type Selector Modal */}
          <Modal
            visible={businessModalVisible}
            onClose={() => setBusinessModalVisible(false)}
          >
            <BusinessTypeSelector
              onClose={() => setBusinessModalVisible(false)}
              featureToUpdate={showFeatures}
            />
          </Modal>

          {!isPasswordChanged && !isRootUser && isPasswordModalShown && (
            <SubscriptionModal>
              <ChangePassword
                data-testid="change-password-modal"
                onClose={() => setIsPasswordModalShown(false)}
              />
            </SubscriptionModal>
          )}
        </>
      )}
    </UseAuthContext.Provider>
  );
};
